import {
  Button,
  Container,
  CustomBox,
  InputTextField,
  LoadingSpinner,
} from '@/components';
import { useTranslation } from 'react-i18next';
import { getDeliveryOrderContainerData } from '@/services/getDeliveryOrderData';
import { useToastNotification } from '@/hooks';
import {
  faDownload,
  faFile,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons';
import { useMutation } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import styles from './DeliveryOrder.module.scss';

type QueryType = {
  marca: string;
  numero: string;
};

const DeliveryOrder = () => {
  const {
    data = '',
    mutate,
    isError,
    isSuccess,
    isPending,
  } = useMutation({
    mutationFn: getDeliveryOrderContainerData,
  });

  const { t } = useTranslation('deliveryOrder');
  const [value, setValue] = useState('');
  const [query, setQuery] = useState<QueryType>({ marca: '', numero: '' });
  const { showToast } = useToastNotification();
  const regexValidate = /^[A-Za-z]{4}\d{7}$/;

  useEffect(() => {
    if (isError) {
      showToast({
        severity: 'error',
        detail: t('unexpectedError'),
      });
    }
  }, [isError, t, showToast]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { value: inputValue } = e.target;
    inputValue = inputValue?.replace(/[^A-Za-z0-9]/g, '');

    if (inputValue?.length > 11) return;

    const marca = inputValue.slice(0, 4);
    const numero = inputValue.slice(4, 11);

    if (marca && !marca.match(/^[A-Za-z]+$/)) return;
    if (numero && !numero.match(/^[0-9]+$/)) return;

    setValue(inputValue?.toUpperCase());
  };

  const onSearch = () => {
    if (!regexValidate.test(value)) {
      showToast({
        severity: 'error',
        detail: t('error'),
      });
      return;
    }
    const regex = /^[^\d]*$/;
    if (typeof value !== 'string') return;
    const marca = value.slice(0, 4);
    const numero = value.slice(4);
    if (numero.length < 7) return;
    if (Number.isNaN(Number(numero))) return;

    if (!regex.test(marca)) return;
    const body = { marca, numero };

    setQuery(body);
    mutate(body);
  };

  const downloadPdf = () => {
    if (!data) return;

    const byteCharacters = atob(data.replace(/-/g, '+').replace(/_/g, '/'));
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i += 1) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    const blob = new Blob([byteArray], { type: 'application/pdf' });

    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `${value}.pdf`;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  return (
    <Container title={t('title')} className={styles.numberStock}>
      <div className={styles.containerSearch}>
        <div className="flex flex-column  md:flex-row col-12 p-0 ">
          <div className="col-12 md: col-12 flex align-items-center pl-0">
            <InputTextField
              className={`col-10 md:col-5 pl-0 ${styles.inputText} `}
              label={t('container')}
              id="container"
              onChange={onInputChange}
              value={value}
              name=""
            />

            <Button
              className={`col-2 md:col-1 ${styles.buttonStock}`}
              onClick={onSearch}
              disabled={!regexValidate.test(value)}
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <span className="hidden md:block">{t('consult')}</span>
            </Button>
          </div>
        </div>
        {isPending && (
          <div
            style={{ zIndex: 10, background: 'rgba(0,0,0, 0.2)' }}
            className="absolute top-0 bottom-0 left-0 right-0 z-10 flex align-items-center justify-content-center"
          >
            <LoadingSpinner />
          </div>
        )}

        {isSuccess && (
          <div className="flex flex-column md:flex-row col-12 pl-0 pr-0 pb-0 pt-3 ">
            <div className="col-12 md:col-6  pl-0 pr-0 ">
              <CustomBox
                variant
                label={`${t('numberConatiner')} ${query?.marca}${query?.numero} ${t('available')}`}
                icon={<FontAwesomeIcon icon={faFile} size="xl" />}
                iconColor="#223466"
                description=""
                footer={
                  <div className={styles.containerButtonDownload}>
                    <Button
                      className={styles.buttonDownload}
                      onClick={downloadPdf}
                    >
                      <FontAwesomeIcon icon={faDownload} />
                      <span>{t('buttonDonwload')}</span>
                    </Button>
                  </div>
                }
                backgroundColor="rgba(71, 128, 191, 0.1)"
              />
            </div>
          </div>
        )}
      </div>
    </Container>
  );
};

export default DeliveryOrder;
